'use client'

import styles from './table-styles.module.css'

import clsx from 'clsx'
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'

import {
  ColumnHelper,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { Icon } from '@wartek-id/icon'
import { Table } from '@wartek-id/table'

import { generateColumns, getHeaderClass } from '@/utils/table'
import { BasicDataRow, NestedColumnType } from '@/apis/types'

interface TableWrapperProps {
  columns: NestedColumnType[]
  data: BasicDataRow[]
  withSummary?: boolean
  onRowClick?: (row: Row<BasicDataRow>, type: 'body' | 'summary') => void
  CustomCellRenderer?: ({ row, col }) => JSX.Element
  // Sorting Props
  withSort?: boolean
  manualSorting?: boolean
  sorting?: SortingState
  onSortingChange?: (newSorting: SortingState) => void
  enableSortColumns?: Record<string, number>
}

export const RoundedTable = ({
  columns = [],
  data,
  withSummary = false,
  onRowClick,
  CustomCellRenderer,

  withSort = false,
  manualSorting = false,
  sorting,
  onSortingChange,
  enableSortColumns = {},
}: TableWrapperProps) => {
  const [tableWidth, setTableWidth] = useState<number>(0)

  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const fakeScrollerRef = useRef<HTMLDivElement>(null)

  const columnHelper: ColumnHelper<BasicDataRow> =
    createColumnHelper<BasicDataRow>()

  const tableColumns = useMemo(
    () =>
      generateColumns<BasicDataRow>(
        columnHelper,
        columns,
        CustomCellRenderer,
        enableSortColumns
      ),
    [columnHelper, columns, CustomCellRenderer, enableSortColumns]
  )

  const table = useReactTable<BasicDataRow>({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: manualSorting,
    state: withSort
      ? {
          sorting,
        }
      : undefined,
    onSortingChange: withSort ? onSortingChange : undefined,
    getSortedRowModel: withSort ? getSortedRowModel() : undefined,
  })

  const firstRow = useMemo(() => data[0] || {}, [data])

  const rows = useMemo(
    () => table.getRowModel().rows,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, table]
  )

  const rowsBody = useMemo(
    () =>
      withSummary
        ? rows?.filter((row) => !row?.['original']?.['summaryRow'])
        : rows,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, rows, withSummary]
  )

  const rowSummary = useMemo(
    () =>
      withSummary
        ? rows?.find((row) => !!row?.['original']?.['summaryRow'])
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, rows, withSummary]
  )

  const isRowClickable = typeof onRowClick === 'function'

  const handleScroll = () => {
    const elTable = tableWrapperRef?.current
    const elFakeScroller = fakeScrollerRef?.current
    if (elFakeScroller) {
      elFakeScroller.scrollLeft = elTable?.scrollLeft || 0
    }
  }

  const handleFakeScroll = () => {
    const elTable = tableWrapperRef?.current
    const elFakeScroller = fakeScrollerRef?.current
    if (elTable) {
      elTable.scrollLeft = elFakeScroller?.scrollLeft || 0
    }
  }

  useEffect(() => {
    if (tableWrapperRef?.current) {
      const elTableWidth = tableWrapperRef.current?.scrollWidth || 0
      if (elTableWidth > 0) {
        setTableWidth(elTableWidth)
      }
    }
  }, [data])

  return (
    <>
      <div
        className="overflow-x-auto overflow-y-hidden"
        id="table-top-scroller"
        ref={fakeScrollerRef}
        onScroll={handleFakeScroll}
      >
        <div
          style={{
            width: tableWidth,
            paddingTop: '1px',
            maxHeight: '10px',
          }}
        >
          &nbsp;
        </div>
      </div>
      <div
        className="overflow-auto pb-4"
        id="table-scroller"
        onScroll={handleScroll}
        ref={tableWrapperRef}
      >
        <Table
          fullWidth
          rounded
          bordered
          variant="striped"
          size="sm"
          className={styles.table}
        >
          <thead className={styles.tableHead}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                data-group-count={table.getHeaderGroups().length}
              >
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    // rowSpan={header.rowSpan}
                    className={clsx(
                      getHeaderClass(header.colSpan, firstRow?.[header?.id])
                    )}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className:
                            withSort && header.column.getCanSort()
                              ? 'flex justify-between items-center cursor-pointer select-none'
                              : '',
                          onClick:
                            withSort && header.column.getCanSort()
                              ? header.column.getToggleSortingHandler()
                              : undefined,
                          ['data-testid']: `header-${header.id}`,
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}

                        {withSort && header.column.getCanSort() ? (
                          <>
                            {header.column.getIsSorted() ? (
                              <>
                                {(header.column.getIsSorted() as string) ===
                                'asc' ? (
                                  <Icon>expand_less</Icon>
                                ) : (
                                  <Icon>expand_more</Icon>
                                )}
                              </>
                            ) : (
                              <Icon>unfold_more</Icon>
                            )}
                          </>
                        ) : null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody
            className={
              withSummary
                ? styles.tableBodyWithFooter
                : styles.tableBodyWithoutFooter
            }
          >
            {rowsBody?.map((row) => (
              <Fragment key={row?.id}>
                <tr
                  onClick={() => {
                    if (isRowClickable) {
                      onRowClick?.(row, 'body')
                    }
                  }}
                  data-row-clickable={isRowClickable}
                  className={clsx(isRowClickable ? 'cursor-pointer' : '')}
                >
                  {row
                    ?.getVisibleCells()
                    ?.map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                </tr>
              </Fragment>
            ))}
          </tbody>

          {rowSummary ? (
            <tfoot className={styles.tableFooter}>
              <tr
                data-type="summary-row"
                onClick={() => {
                  if (isRowClickable) {
                    onRowClick?.(rowSummary, 'summary')
                  }
                }}
                className={clsx(isRowClickable ? 'cursor-pointer' : '')}
                data-row-clickable={isRowClickable}
              >
                {rowSummary
                  ?.getVisibleCells()
                  ?.map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
              </tr>
            </tfoot>
          ) : null}
        </Table>
      </div>
    </>
  )
}
